import { Modal } from '@almbrand/modal';
import { MultiColItem } from '@almbrand/multicolitem';
import { Paragraph } from '@almbrand/paragraph';
import { RichText } from '@almbrand/richtext';
import { pushToDataLayer } from '@almbrand/trackinghooks';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { formatPolicyExpiryLabel, formatPolicyPriceLabel } from 'common/PolicyUtil';
import { DownloadTracking, InternalSearchTracking } from 'common/trackingUtils';
import { ExportCsvButton } from 'components/1-atoms/ExportCsvButton/ExportCsvButton';
import { LoadingSpinner } from 'components/1-atoms/LoadingSpinner';
import { PolicyPreviewButton } from 'components/1-atoms/PolicyPreviewButton';
import { PreviewModal } from 'components/2-molecules/PreviewModal/PreviewModal';
import { HttpStatusCode } from 'constantsValues';
import { ThemeNames } from 'constantsValues/themeNames';
import { Charged } from 'generated-api/bff/api';
import usePolicyPreview from 'hooks/usePolicyPreview';
import { useThemeName } from 'hooks/useThemeName';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchPolicyObjectsExport } from 'store/slices/policyObjectsExportSlice';
import { fetchPolicyOverviewDocument } from 'store/slices/policyOverviewDocumentSlice';
import { dataUrlToBlob, downloadBlobFile } from 'utilities/downloadUtils';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import styles from './PolicyListComponent.module.scss';
import { PolicyOverviewHeader } from './PolicyOverviewHeader/PolicyOverviewHeader';

// Define the shape of a policy object
interface Policy {
	title: string;
	subtitle?: string;
	type?: string;
	policy_number?: string;
	price?: {
		amount: number;
		unit: string;
		charged?: Charged;
		title?: string;
	};
	group?: {
		title: string;
		type: string;
	};
	source_system?: string;
	end?: {
		title: string;
		date: string;
	};
	status?: {
		type: string;
	};
}

export interface PolicyListComponentProps {
	mainArea: {
		items: Array<{
			content: {
				title: string;
				icon: {
					url: string;
					properties: {
						altText?: string;
					};
				};
			};
		}>;
	};

	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
	title: string;
}

const PolicyListComponent: React.FC<PolicyListComponentProps> = ({ mainArea, title, statusMessages }) => {
	const policies = useAppSelector((state) => state.policies.policies) as Policy[] | undefined;
	const total = useAppSelector((state) => state.policies.total);
	const status = useAppSelector((state) => state.policies.status);
	const dispatch = useAppDispatch();
	const { isMobile, isTablet } = useDeviceQuery();
	const themeName = useThemeName();
	const isAlmbrand = themeName === ThemeNames.almbrand;
	const isCodan = themeName === ThemeNames.codan;

	const {
		openModal,
		previewDocument,
		handlePolicyClick,
		handlePolicyDownload,
		openBlobPreview,
		onModalClose,
		resetError,
		error,
	} = usePolicyPreview();

	const [searchTerm, setSearchTerm] = useState<string>(''); // State for search input

	// SearchTracking
	useEffect(() => {
		if (searchTerm === '') return; // Don't track empty searches

		const timeoutId = setTimeout(() => {
			pushToDataLayer(InternalSearchTracking(searchTerm, 'search results', themeName));
		}, 2000); // 2000ms delay

		return () => clearTimeout(timeoutId); // Cleanup timeout on searchTerm change
	}, [searchTerm, themeName]);

	const IconsList = mainArea.items;

	// Group policies by `policy.group.title`
	const groupedPolicies = policies?.reduce((policyGroups: Record<string, Policy[]>, policy) => {
		const groupTitle = policy.group?.title || 'Andet';

		if (!policyGroups[groupTitle]) {
			policyGroups[groupTitle] = [];
		}
		policyGroups[groupTitle].push(policy);
		return policyGroups;
	}, {});

	// Filter grouped policies based on the search term
	const filteredGroupedPolicies = Object.entries(groupedPolicies || {}).reduce(
		(filteredPolicyGroups: Record<string, Policy[]>, [groupTitle, groupPolicies]) => {
			const groupMatches = groupTitle.toLowerCase().includes(searchTerm.toLowerCase());

			const filteredPolicies = groupPolicies.filter(
				(policy) =>
					policy.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.policy_number?.includes(searchTerm)
			);

			if (groupMatches || filteredPolicies.length > 0) {
				filteredPolicyGroups[groupTitle] = filteredPolicies;
			}

			return filteredPolicyGroups;
		},
		{}
	);

	const handlePolicyAction = (policy: Policy) => {
		const downloadTracking = DownloadTracking(
			'policy',
			'pdf',
			isMobile || isTablet ? 'download' : 'preview',
			themeName
		);

		handlePolicyClick(policy.policy_number, policy.title, downloadTracking);
	};

	const handleExportObjects = async (policy: Policy) => {
		if (!policy.policy_number) return;
		try {
			const payload = await dispatch(fetchPolicyObjectsExport({ policyNo: policy.policy_number })).unwrap();
			downloadBlobFile({ data: payload.data, fileName: payload.fileName });
		} catch (e) {}
	};

	// Preview (or download on mobile) policy overview handler using generic blob preview
	const handleDownloadOverview = async () => {
		try {
			const { dataUrl, fileName } = await dispatch(fetchPolicyOverviewDocument()).unwrap();
			const blob = dataUrlToBlob(dataUrl); // Removed fetch()
			await openBlobPreview({
				blob,
				name: fileName,
				contentType: 'application/pdf',
				displayAsPdf: true,
				closeLabel: 'Luk oversigt',
				downloadLabel: 'Download oversigt',
				downloadTrackingData: DownloadTracking(
					'policy-overview',
					'pdf',
					isMobile || isTablet ? 'download' : 'preview',
					themeName
				),
			});
		} catch (e) {}
	};

	const getGroupIcon = (groupTitle: string) => {
		const groupType = groupedPolicies[groupTitle]?.[0]?.group?.type || '';
		return IconsList.find((item) => item.content.title === groupType)?.content?.icon?.url || '';
	};

	const shouldShowStatusMessage =
		status !== HttpStatusCode.Loading &&
		(status === HttpStatusCode.InternalServerError ||
			status === HttpStatusCode.NoContent ||
			Object.keys(filteredGroupedPolicies)?.length === 0);

	const shouldShowHealthTag = (groupTitle: string, groupPolicies: Policy[]): boolean =>
		isCodan && groupTitle === 'Sundhed' && groupPolicies.some((policy) => policy.title === 'Sundhedsforsikring');

	const pendingTagLabel = isMobile ? 'Afventer' : 'Afventer, ikke i kraft';

	return (
		<>
			<PolicyOverviewHeader
				title={title}
				total={total}
				shouldShowStatusMessage={shouldShowStatusMessage}
				setSearchTerm={setSearchTerm}
				handleDownloadOverview={handleDownloadOverview}
				isLoading={status === HttpStatusCode.Loading}
				oneTimePoliciesCount={policies?.filter((p) => p.price?.charged === Charged.Once).length ?? 0}
			/>

			{status === HttpStatusCode.Loading && <LoadingSpinner />}

			{shouldShowStatusMessage && (
				<RichText
					className={classNames(styles.PolicyList__statusMessage, 'RichText')}
					description={getStatusMessage(
						statusMessages,
						status == HttpStatusCode.OK && Object.keys(filteredGroupedPolicies)?.length < 1
							? HttpStatusCode.NoContent
							: status
					)}
				/>
			)}

			{!shouldShowStatusMessage &&
				Object.entries(filteredGroupedPolicies)
					.sort(([a], [b]) => a.localeCompare(b, 'da'))
					.map(([groupTitle, groupPolicies]) => {
						const showHealthTag = shouldShowHealthTag(groupTitle, groupPolicies);

						return (
							<div
								key={groupTitle}
								className={styles.PolicyGroup}
							>
								<div className={styles.PolicyGroup__header}>
									<img
										src={getGroupIcon(groupTitle)}
										width={isAlmbrand ? 32 : 56}
										height={isAlmbrand ? 32 : 56}
									/>

									<Paragraph
										text={`${groupTitle} (${groupPolicies.length})`}
										fontColor='primaryDark'
										paragraphType={{ type: 'title', kind: 'title' }}
									/>
									{showHealthTag && (
										<span className={styles.PolicyList__groupTag}>
											Medarbejderlister kan downloades
										</span>
									)}
								</div>

								<div className={styles.PolicyList}>
									{groupPolicies.map((policy, index) => (
										<MultiColItem
											key={index}
											className={styles.PolicyList__item}
											title={policy.title}
											subtitle={policy.subtitle}
											{...(policy.status?.type === 'PENDING' && {
												pendingTag: pendingTagLabel,
											})}
											paragraphSize={isAlmbrand ? 'default' : 'sm'}
											additionalInfo={[
												{
													text: formatPolicyPriceLabel(policy.price),
													paragraphType: {
														kind: 'paragraph',
														size: isAlmbrand ? 'default' : 'sm',
													},
													fontColor: 'primaryDark',
												},
												{
													text: formatPolicyExpiryLabel(policy.price, policy.end?.date),
													paragraphType: {
														kind: 'paragraph',
														size: isAlmbrand ? 'default' : 'sm',
													},
													fontColor: 'secondaryDarkGrey',
												},
											]}
											onClick={() => handlePolicyAction(policy)}
											{...(isMobile
												? {}
												: {
														action: (
															<div className={styles.PolicyList__actionContainer}>
																{isCodan && policy.title === 'Sundhedsforsikring' && (
																	<ExportCsvButton
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			handleExportObjects(policy);
																		}}
																		title='Eksporter CSV'
																	/>
																)}
																<PolicyPreviewButton
																	policyNumber={`Police: ${policy.policy_number}`}
																/>
															</div>
														),
													})}
										/>
									))}
								</div>
							</div>
						);
					})}

			{openModal && (
				<PreviewModal
					displayAsPdf={previewDocument.displayAsPdf}
					data={previewDocument.data}
					handleMessageDownload={handlePolicyDownload}
					onClose={onModalClose}
					closeLabel={previewDocument.closeLabel}
					downloadLabel={previewDocument.downloadLabel}
				/>
			)}

			{error && (
				<Modal
					handleClose={() => resetError()}
					mode={'feedback'}
					mobileVariation={'dialog'}
				>
					<RichText description={getStatusMessage(statusMessages, HttpStatusCode.NotFound)} />
				</Modal>
			)}
		</>
	);
};

export default PolicyListComponent;
